/**
 * Fechas de negocio en hora de Caracas (bloque 12 del plan de entrega): días hábiles para el plazo de retiro del
 * material («Dispone de 5 días hábiles para retirar el material»).
 */
const DIA_MS = 86_400_000;

/** Fecha local de Caracas (AAAA-MM-DD) de un instante. */
export function fechaCaracas(instante: Date | string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas' }).format(typeof instante === 'string' ? new Date(instante) : instante);
}

/**
 * Días hábiles (lunes a viernes) transcurridos DESPUÉS del día `desde`, hasta el día `hasta` inclusive, en hora de
 * Caracas. Una venta del viernes lleva 1 día hábil el lunes; el mismo día de la venta lleva 0. No conoce feriados.
 */
export function diasHabilesEntre(desde: Date | string, hasta: Date | string): number {
  const a = Date.parse(`${fechaCaracas(desde)}T00:00:00Z`);
  const b = Date.parse(`${fechaCaracas(hasta)}T00:00:00Z`);
  let n = 0;
  for (let t = a + DIA_MS; t <= b; t += DIA_MS) {
    const dia = new Date(t).getUTCDay();
    if (dia !== 0 && dia !== 6) n += 1;
  }
  return n;
}

/**
 * Fecha de la retención (Providencia SNAT/2025/000054, arts. 13 y 14: se retiene en el pago o abono en cuenta y se
 * declara por quincena). Decide la quincena del TXT del IVA y es la FechaOperacion del XML del ISLR, que debe caer en
 * el período declarado: la fecha fiscal de la compra si cae en el período del comprobante; si el comprobante es de un
 * período posterior (se numeró después), el día en que se numeró, sin salir de ese período.
 */
export function fechaRetencion(fechaFiscal: string, numeradoEn: string | Date | null, periodo: string): string {
  const anio = Number(periodo.slice(0, 4));
  const mes = Number(periodo.slice(4, 6));
  const ini = `${periodo.slice(0, 4)}-${periodo.slice(4, 6)}-01`;
  const fin = `${periodo.slice(0, 4)}-${periodo.slice(4, 6)}-${String(new Date(Date.UTC(anio, mes, 0)).getUTCDate()).padStart(2, '0')}`;
  if (fechaFiscal >= ini && fechaFiscal <= fin) return fechaFiscal;
  const numerado = numeradoEn ? fechaCaracas(numeradoEn) : fin;
  return numerado < ini ? ini : numerado > fin ? fin : numerado;
}

/** Quincena de una fecha AAAA-MM-DD: 1 (del 1 al 15) o 2 (del 16 al último día). */
export const quincenaDe = (fecha: string): '1' | '2' => (Number(fecha.slice(8, 10)) <= 15 ? '1' : '2');

/** Fecha AAAA-MM-DD como la lee una persona en Venezuela: DD/MM/AAAA (mensajes y avisos; hallazgo G2 del 2026-10-18). */
export const diaLegible = (fecha: string): string => `${fecha.slice(8, 10)}/${fecha.slice(5, 7)}/${fecha.slice(0, 4)}`;

/** Fecha y hora de Caracas «AAAA-MM-DD HH:mm» de un instante (Excel y exportes; hallazgo B10 del 2026-10-18). */
export function fechaHoraCaracas(instante: Date | string): string {
  const d = typeof instante === 'string' ? new Date(instante) : instante;
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Caracas', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}
